import { isEmpty, isEqual } from 'lodash';
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Subject, distinctUntilChanged } from 'rxjs';
import { i18nKeyType } from '../../i18n';
import { runInBackground } from '../../util/async';
import { logger } from '../../util/logger';
import {
  DirectConnectPollHealthOptions,
  pollDirectConnectHealth,
} from '../api/direct-connect-health-api';
import { DirectConnectHealthStatus } from '../model/direct-connect.model';
import {
  DirectConnectGateSource,
  useDirectConnectGateKeeper,
} from './direct-connect-gate-keeper';

export interface DirectConnectHealthGateProps {
  url: string;
  children: ReactNode;
  loading?: {
    message?: i18nKeyType;
  };
}

interface DirectContextHealthGateContextValue {
  healthUrl: string;
  healthStatus?: DirectConnectHealthStatus;
  startHealthMonitor: () => void;
  stopHealthMonitor: () => void;
  forceHealthCheck: (options?: DirectConnectPollHealthOptions) => void;
}

const DirectConnectHealthContext = createContext<
  DirectContextHealthGateContextValue | undefined
>(undefined);

const DirectConnectHealthGate = (props: DirectConnectHealthGateProps) => {
  const { children, url } = props;

  const [checkComplete, setCheckComplete] = useState<boolean>(false);
  const [hasConnectedOnce, setHasConnectedOnce] = useState<boolean>(false);
  const [healthStatus, setHealthStatus] = useState<
    DirectConnectHealthStatus | undefined
  >(undefined);

  const { reportGateStatus } = useDirectConnectGateKeeper();

  const isMonitorRunningRef = useRef<boolean>(false);

  const { i18n } = useTranslation();

  const isConnected = useMemo(() => {
    return healthStatus !== undefined;
  }, [healthStatus]);

  const checkHealthWithOptions = useCallback(
    (options: DirectConnectPollHealthOptions) => {
      runInBackground(async () => {
        setCheckComplete(false);

        const result = await pollDirectConnectHealth(url, options);

        cachedHealthStatus = result;
        machineHealthSubject$.next(cachedHealthStatus);

        if (result !== undefined) {
          setHasConnectedOnce(true);
        }

        setHealthStatus(result);
        setCheckComplete(true);
      });
    },
    [url]
  );

  const retryHealthCheck = useCallback(
    async (options: DirectConnectPollHealthOptions = {}) => {
      checkHealthWithOptions({
        timeout: 10_000,
        ...options,
      });
    },
    [checkHealthWithOptions]
  );

  useEffect(() => {
    const interval = setInterval(() => {
      if (isMonitorRunningRef.current) {
        checkHealthWithOptions({
          timeout: 5_000,
        });
      }
    }, 30_000);

    return () => {
      if (interval) {
        clearInterval(interval);
      }

      cachedHealthStatus = undefined;
      machineHealthSubject$.next(cachedHealthStatus);
    };
  }, [checkHealthWithOptions]);

  const startHealthMonitor = useCallback(() => {
    if (!isMonitorRunningRef.current) {
      logger.info('starting health gate monitor');
      isMonitorRunningRef.current = true;
    }
  }, []);

  const stopHealthMonitor = useCallback(() => {
    if (isMonitorRunningRef.current) {
      logger.info('stopping health gate monitor');
      isMonitorRunningRef.current = false;
    }
  }, []);

  // kick off initial health check
  useEffect(() => {
    retryHealthCheck();
  }, [retryHealthCheck]);

  const contextValue = useMemo<DirectContextHealthGateContextValue>(() => {
    return {
      healthUrl: url,
      healthStatus,
      forceHealthCheck: retryHealthCheck,
      startHealthMonitor,
      stopHealthMonitor,
    };
  }, [
    healthStatus,
    url,
    retryHealthCheck,
    startHealthMonitor,
    stopHealthMonitor,
  ]);

  const showLoader = !checkComplete && !isConnected;
  const showError = checkComplete && !isConnected;
  const showChildren = (checkComplete && isConnected) || hasConnectedOnce;
  const showBackground = !showChildren;

  useEffect(() => {
    logger.info('direct connect health gate status', {
      isConnected,
      checkComplete,
      hasConnectedOnce,
      showLoader,
      showError,
      showChildren,
      showBackground,
    });
  }, [
    isConnected,
    checkComplete,
    hasConnectedOnce,
    showLoader,
    showError,
    showChildren,
    showBackground,
  ]);

  useEffect(() => {
    reportGateStatus({
      source: DirectConnectGateSource.HEALTH_GATE,
      failed: {
        show: showError,
        props: {
          retry: retryHealthCheck,
          title: i18n.t('common:direct_connect'),
          description: i18n.t('common:direct_connect_health_check_failed'),
        },
      },
      loader: {
        show: showLoader,
        message: i18n.t(
          props.loading?.message ?? 'common:direct_connect_connecting_to_robot'
        ),
      },
      background: {
        show: showBackground,
      },
    });
  }, [
    reportGateStatus,
    showError,
    retryHealthCheck,
    i18n,
    stopHealthMonitor,
    showLoader,
    showBackground,
    props.loading?.message,
  ]);

  return (
    <DirectConnectHealthContext.Provider value={contextValue}>
      {showChildren && children}
    </DirectConnectHealthContext.Provider>
  );
};

const useDirectConnectHealthGateContext =
  (): DirectContextHealthGateContextValue => {
    return useContext(
      DirectConnectHealthContext
    ) as DirectContextHealthGateContextValue;
  };

const machineHealthSubject$ = new Subject<
  DirectConnectHealthStatus | undefined
>();

// This provides the last polled value for new subscribers.
let cachedHealthStatus: DirectConnectHealthStatus | undefined = undefined;

const useDirectConnectCurrentMachineHealth = () => {
  const [healthStatus, setHealthStatus] = useState<
    DirectConnectHealthStatus | undefined
  >(cachedHealthStatus);

  useEffect(() => {
    const subscription = machineHealthSubject$
      .pipe(distinctUntilChanged(isEqual))
      .subscribe((status) => {
        if (isEmpty(status)) {
          setHealthStatus(undefined);
        }
        setHealthStatus(status);
      });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return healthStatus;
};

export {
  DirectConnectHealthGate,
  useDirectConnectHealthGateContext,
  useDirectConnectCurrentMachineHealth,
};
